/**
 * Export a Excel (.xlsx) del tablero "Mi tienda" y sus modales (Fase D, plan
 * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan` — espejo VERBATIM del
 * app, ver `cdr-sa-app/src/features/indicadores/utils/exportXlsx.ts`).
 *
 * `exportXlsx` carga la librería SheetJS (`xlsx@0.18.5`) con `import()`
 * DINÁMICO — a propósito NO es un import estático — para que el parser/writer
 * (~varios cientos de KB) quede en un chunk separado del bundle inicial y solo
 * se descargue cuando el usuario de verdad hace clic en "Exportar".
 *
 * Los `buildXSheet` de abajo son PUROS (sin `import()`, sin DOM, sin red) a
 * propósito: son lo que cubren los tests (`exportXlsx.test.ts`) sin mockear
 * nada más que, para `exportXlsx` mismo, el módulo `xlsx`.
 *
 * Regla dura del proyecto (CLAUDE.md / Global Constraints del plan): NINGÚN
 * monto `$` en ninguna celda exportada — todo lo que sale de aquí son
 * conteos/pares/porcentajes, igual que en la UI.
 */
import type {
  NegadoProductoRow,
  NegadoTallaRow,
  TiendaVendedor,
  VendedorDetalleProducto,
  VendedorProductoTalla,
} from '../types/indicadores.types';
import type { DiaNegados, TallaNegados } from './negadosDrill';
import type { SucursalTableRow } from './tabla';

/** Una hoja del workbook a exportar: nombre + encabezado + filas (mismo orden de columnas). */
export interface SheetSpec {
  name: string;
  header: string[];
  rows: (string | number)[][];
}

/**
 * Nombre de hoja válido para Excel: máximo 31 caracteres y sin los
 * caracteres reservados `[ ] : * ? / \`. Si el nombre saneado queda vacío
 * (ej. el original era solo caracteres reservados), cae a `'Hoja'`.
 */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, '').trim();
  const truncated = cleaned.slice(0, 31);
  return truncated || 'Hoja';
}

/**
 * Construye el workbook (una hoja por `SheetSpec`, header + rows via
 * `aoa_to_sheet`) y dispara la descarga (`XLSX.writeFile`). El `import('xlsx')`
 * es DINÁMICO a propósito (ver comentario del módulo) — se mockea en el test.
 *
 * Sheets con 0 filas SÍ se escriben (solo con el header) — consistente para
 * las 5 vistas: un export nunca "desaparece" una hoja solo porque esa parte
 * del dato vino vacía, el usuario ve la tabla vacía en Excel en vez de un
 * archivo con menos hojas de las que esperaba.
 */
export async function exportXlsx(sheets: SheetSpec[], filename: string): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const ws = XLSX.utils.aoa_to_sheet([sheet.header, ...sheet.rows]);
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheet.name));
  }
  XLSX.writeFile(wb, filename);
}

/**
 * Hoja "Vendedores" de Mi tienda (`Vendedores.tsx`) — una fila por vendedor
 * del ranking, con las mismas columnas/derivados que pinta la card (mirror de
 * `utils/vendedores.ts::buildVendedorCards`, SIN los derivados puramente
 * visuales como colores/anchos de barra):
 * - `efectividad` = round(S/C*100) —«Efectividad de inventario», la de la
 *   tienda— si la API manda `solicitudes` por vendedor (12-oct); si no,
 *   round(A/C*100) con el nombre «Consultas atendidas». `0` si `consultas` es 0.
 *   El encabezado dice cuál de las dos es: nunca dos fórmulas con un nombre.
 * - `tasaAtencion` = round(ventaPares/atendidas*100), celda vacía `''` si
 *   `atendidas` es 0 (igual que la UI pinta "—%" — aquí sin el símbolo `%`
 *   ni el `$`, la celda es solo el número o vacía).
 * Sin `$` en ninguna celda (regla dura).
 */
export function buildVendedoresSheet(vendedores: TiendaVendedor[]): SheetSpec {
  const conS = vendedores.some((v) => typeof v.solicitudes === 'number');
  // Tickets (SPEC 2026-10-13, d): la columna existe si la API los manda; una
  // celda vacía es «no se saben», nunca 0.
  const conTickets = vendedores.some((v) => v.tickets !== undefined);
  const header = [
    'Código',
    'Vendedor',
    'Consultas',
    'Surtió',
    'Negados',
    conS ? 'Efectividad de inventario % (S ÷ C)' : 'Consultas atendidas % (A ÷ C)',
    'Pares vendidos',
    'Contado',
    'Crédito',
    'Nuevos a crédito',
    'Tasa de atención %',
    ...(conTickets ? ['Tickets'] : []),
  ];
  const rows = vendedores.map((v): (string | number)[] => {
    const numerador = conS ? v.solicitudes ?? 0 : v.atendidas;
    const efectividad = v.consultas > 0 ? Math.round((numerador / v.consultas) * 100) : 0;
    const tasaAtencion = v.atendidas > 0 ? Math.round((v.ventaPares / v.atendidas) * 100) : '';
    return [
      v.codigo || v.externalUserId,
      v.nombre ?? v.externalUserId,
      v.consultas,
      v.atendidas,
      v.negados,
      efectividad,
      v.ventaPares,
      v.ventaContado,
      v.ventaCredito,
      v.clientesNuevos,
      tasaAtencion,
      ...(conTickets ? [v.tickets ?? ''] : []),
    ];
  });
  return { name: 'Vendedores', header, rows };
}

/** Hoja "Negados" de Mi tienda (`NegadosTiendaTable.tsx`) — una fila por producto negado. */
export function buildNegadosTiendaSheet(productos: NegadoProductoRow[]): SheetSpec {
  const header = ['ICOD', 'Descripción', 'Estatus', 'Negados'];
  const rows = productos.map((p): (string | number)[] => [p.icod, p.descripcion, p.status, p.negados]);
  return { name: 'Negados', header, rows };
}

/** Hoja "Detalle vendedor" (`VendedorDetalleModal.tsx`) — trazabilidad por producto de UN vendedor. */
export function buildVendedorDetalleSheet(productos: VendedorDetalleProducto[]): SheetSpec {
  const header = ['ICOD', 'Descripción', 'Estatus', 'Consultó', 'Surtió', 'Negó', 'Vendió'];
  const rows = productos.map((p): (string | number)[] => [
    p.icod,
    p.descripcion || p.icod,
    p.status,
    p.consultas,
    p.atendidas,
    p.negados,
    p.vendido,
  ]);
  return { name: 'Detalle vendedor', header, rows };
}

/** Totales de la corrida por talla (mismo shape que `VendedorProductoDetalleResponse.totales`). */
export interface ModeloTallaTotales {
  consultas: number;
  atendidas: number;
  negados: number;
  vendido: number;
}

/** Hoja "Corrida por talla" (`ModeloDetalleModal.tsx`) — una fila por talla + fila final de totales. */
export function buildModeloTallaSheet(tallas: VendedorProductoTalla[], totales: ModeloTallaTotales): SheetSpec {
  const header = ['Talla', 'Consultó', 'Surtió', 'Negó', 'Vendió'];
  const rows: (string | number)[][] = tallas.map((t) => [t.talla, t.consultas, t.atendidas, t.negados, t.vendido]);
  rows.push(['TOTAL', totales.consultas, totales.atendidas, totales.negados, totales.vendido]);
  return { name: 'Corrida por talla', header, rows };
}

/**
 * 2 hojas de `NegadosDrillModal.tsx` — "Por talla" y "Por día", de la misma
 * data ya agregada que pinta el modal (`aggregatePorTalla`/`serieContinua` de
 * `utils/negadosDrill.ts`), sin volver a agregar nada aquí. Reusa
 * `TallaNegados`/`DiaNegados` de `./negadosDrill` (type-only, sin costo de
 * bundle) en vez de redeclarar tipos estructuralmente idénticos.
 */
export function buildNegadosDrillSheets(porTalla: TallaNegados[], porDia: DiaNegados[]): SheetSpec[] {
  const porTallaSheet: SheetSpec = {
    name: 'Por talla',
    header: ['Talla', 'Negados'],
    rows: porTalla.map((t): (string | number)[] => [t.talla, t.negados]),
  };
  const porDiaSheet: SheetSpec = {
    name: 'Por día',
    header: ['Fecha', 'Negados'],
    rows: porDia.map((d): (string | number)[] => [d.fecha, d.negados]),
  };
  return [porTallaSheet, porDiaSheet];
}

/**
 * Hoja "Sucursales" del Global (`TablaSucursales.tsx`, ítem 5): una fila por
 * sucursal con las MISMAS columnas que la tabla en pantalla. Se exportan
 * TODAS las sucursales del periodo/filtro, no sólo el top 40 que cabe en la
 * tabla — el usuario pidió el export justamente para llevarse el detalle
 * completo. Los números van CRUDOS (sin formatear): Excel debe poder sumar y
 * graficar, así que los porcentajes viajan como fracción 0..1 y los montos
 * como número, no como texto con `$`.
 */
export function buildSucursalesSheet(rows: SucursalTableRow[]): SheetSpec {
  // Rótulos del glosario (12-oct), con la fórmula: el Excel viaja solo y quien
  // lo abre no tiene el ícono «i» de la pantalla.
  const header = [
    'Sucursal',
    'Cadena · Supervisor',
    'Consultas',
    'Solicitudes a bodega',
    'Solicitudes atendidas',
    'Negados',
    'Venta (pares)',
    'Contado $',
    'Crédito $',
    'Tasa de atención (V ÷ A)',
    'Efectividad de inventario (S ÷ C)',
    '% atendidas (A ÷ S)',
    'Tiempo prom. (seg)',
    ...(rows.some((r) => r.tickets !== undefined)
      ? ['Tickets (documentos T, préstamos incluidos)']
      : []),
  ];
  const conTickets = rows.some((r) => r.tickets !== undefined);
  const rowsOut = rows.map((r): (string | number)[] => [
    r.name,
    r.sub,
    r.consultas,
    r.solicitudes,
    r.atendidas,
    r.negados,
    r.venta ?? '',
    r.contadoMonto ?? '',
    r.creditoMonto ?? '',
    r.tasa ?? '',
    r.efect ?? '',
    r.sla ?? '',
    r.tiempo ?? '',
    ...(conTickets ? [r.tickets ?? ''] : []),
  ]);
  return { name: 'Sucursales', header, rows: rowsOut };
}

/**
 * Hoja "Productos negados" del Global (`NegadosCards.tsx`, ítem 5). Ojo: el
 * endpoint ya devuelve el TOP 40 recortado server-side, así que esto exporta
 * lo que la tarjeta tiene, no el universo completo de negados.
 */
export function buildNegadosProductosSheet(productos: NegadoProductoRow[]): SheetSpec {
  const header = ['ICOD', 'Descripción', 'Estatus', 'Negados'];
  const rows = productos.map((p): (string | number)[] => [
    p.icod,
    p.descripcion,
    p.status,
    p.negados,
  ]);
  return { name: 'Productos negados', header, rows };
}

/** Hoja "Negados por talla" del Global (`NegadosCards.tsx`, ítem 5). También top 40 server-side. */
export function buildNegadosTallasSheet(tallas: NegadoTallaRow[]): SheetSpec {
  const header = ['Sucursal', 'Descripción', 'Talla', 'Negados'];
  const rows = tallas.map((t): (string | number)[] => [
    t.sucursal ?? '',
    t.descripcion,
    t.talla,
    t.negados,
  ]);
  return { name: 'Negados por talla', header, rows };
}
